import React, { useState } from 'react';
import { NavigateFunction } from 'react-router-dom';
import { Dialog, Grow, IconButton } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { getActiveAppBrandConfig, getActiveBrandConfig } from '../../config/environment.config';
import { useTailwindBrand } from '../shared';
import { useBrandAuthConfig } from '../../Hooks/Auth/useBrandAuthConfig';

interface TypeAccountProps {
    navigate: NavigateFunction
    children?: string
    /**
     * Espace client : quand la page d'inscription est ouverte sans rôle dans
     * l'URL (?=client ou ?=prestataire), le dialogue s'affiche directement,
     * sans bouton déclencheur.
     */
    forceOpen?: boolean
}

const Transition = React.forwardRef(function Transition(
    props: any,
    ref: React.Ref<unknown>
) {
    return <Grow ref={ref} {...props} timeout={350} />;
});

/**
 * Choix du type de compte à l'inscription. La sélection ajoute le rôle à
 * l'URL d'inscription commune : /register?=client ou /register?=prestataire.
 */
const TypeAccount = ({ navigate, children, forceOpen = false }: TypeAccountProps) => {
    const { classes, brandConfig } = useTailwindBrand();
    const { arrayChoice } = useBrandAuthConfig();
    const [open, setOpen] = useState(false);
    const [hovered, setHovered] = useState<number | null>(null);
    const isOpen = forceOpen || open;
    const primary = brandConfig.colors?.primary ?? '#0054A3';

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        if (forceOpen) {
            // pas de choix : retour à la connexion
            navigate('/');
            return;
        }
        setOpen(false);
    };

    const handleSelect = (statut?: string) => {
        setOpen(false);
        // le rôle choisi est porté par l'URL d'inscription commune
        navigate(`/${getActiveBrandConfig()}/register?=${statut === 'client' ? 'client' : 'prestataire'}`);
    };

    return (
        <>
            {!forceOpen && (
                <button children={children} onClick={handleOpen} className={`underline cursor-pointer ${classes.primaryText} font-bold`} />
            )}
            <Dialog
                open={isOpen}
                onClose={handleClose}
                TransitionComponent={Transition}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        overflow: 'hidden',
                        boxShadow: '0 32px 64px -12px rgba(0,0,0,0.35)',
                    },
                }}
                slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', backgroundColor: 'rgba(15,23,42,0.45)' } } }}
            >
                {/* En-tête en dégradé aux couleurs de la marque */}
                <div
                    className="relative px-8 pt-10 pb-14 text-center overflow-hidden"
                    style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}D9 55%, ${primary}B3 100%)`, fontFamily: brandConfig.fontFamily }}
                >
                    {/* halos décoratifs */}
                    <div className="absolute -top-16 -left-16 w-56 h-56 rounded-full" style={{ background: 'rgba(255,255,255,0.12)' }} />
                    <div className="absolute -bottom-20 -right-10 w-72 h-72 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }} />
                    <IconButton
                        onClick={handleClose}
                        aria-label="Fermer"
                        size="small"
                        sx={{ position: 'absolute', top: 12, right: 12, color: 'rgba(255,255,255,0.85)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' } }}
                    >
                        <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                    <p className="relative text-white/80 text-sm font-semibold tracking-widest uppercase mb-2">Bienvenue</p>
                    <h2 className="relative text-white font-extrabold lg:text-3xl text-2xl leading-tight">
                        Démarrez l'aventure {getActiveAppBrandConfig()}
                    </h2>
                    <p className={`font-bold relative text-white lg:text-base text-sm mt-2 max-w-lg mx-auto`}>
                        Dites-nous qui vous êtes, nous préparons le reste.
                    </p>
                </div>

                {/* Cartes de choix, à cheval sur l'en-tête */}
                <div className="px-6 lg:px-10 pb-8 -mt-8 relative" style={{ fontFamily: brandConfig.fontFamily }}>
                    <div className="grid lg:grid-cols-2 grid-cols-1 gap-4">
                        {arrayChoice.map((item, index) => {
                            const isHovered = hovered === index;
                            return (
                                <button
                                    key={index}
                                    type="button"
                                    onClick={() => handleSelect(item.statut)}
                                    onMouseEnter={() => setHovered(index)}
                                    onMouseLeave={() => setHovered(null)}
                                    className="group relative text-left bg-white rounded-2xl p-6 transition-all duration-300 ease-out cursor-pointer focus:outline-none"
                                    style={{
                                        border: `2px solid ${isHovered ? primary : 'rgba(226,232,240,1)'}`,
                                        boxShadow: isHovered
                                            ? `0 20px 40px -12px ${primary}55`
                                            : '0 8px 24px -12px rgba(15,23,42,0.18)',
                                        transform: isHovered ? 'translateY(-6px)' : 'translateY(0)',
                                    }}
                                >
                                    {/* pastille icône */}
                                    <div
                                        className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl mb-4 transition-transform duration-300 group-hover:scale-110"
                                        style={{ background: `${primary}14` }}
                                    >
                                        {item.icon}
                                    </div>
                                    <p className="font-extrabold lg:text-lg text-base mb-1" style={{ color: isHovered ? primary : brandConfig.colors.text }}>
                                        {item.title}
                                    </p>
                                    <p className={`text-sm  ${classes.text} leading-relaxed pr-6`}>
                                        {item.text}
                                    </p>
                                    <span
                                        className="inline-flex items-center gap-1 mt-4 font-bold text-sm transition-all duration-300"
                                        style={{ color: primary, opacity: isHovered ? 1 : 0.75 }}
                                    >
                                        Commencer
                                        <ArrowForwardRoundedIcon
                                            sx={{ fontSize: 18, transition: 'transform .3s', transform: isHovered ? 'translateX(4px)' : 'none' }}
                                        />
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    <p className={`font-bold text-center text-sm  ${classes.text} mt-6`}>
                        Déjà un compte ?{' '}
                        <button
                            type="button"
                            onClick={() => { setOpen(false); navigate('/'); }}
                            className="font-bold underline underline-offset-2"
                            style={{ color: primary }}
                        >
                            Se connecter
                        </button>
                    </p>
                </div>
            </Dialog>
        </>
    );
};

export default TypeAccount;
